import { c as _c } from "react/compiler-runtime";
import { forwardRef, useCallback, useRef, useState } from 'react';
import { Box, Button, Text, Flex } from '@chakra-ui/react';
import { DialogBody, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { NumberInputField, NumberInputRoot } from '@/components/ui/number-input';
import { Radio, RadioGroup } from '@/components/ui/radio';
import { InformationIcon } from '@/components/util-component/InformationIcon';
import { ExportConfirmationProps, ExportOptions, ExportType } from '.';
type ExportOptionsDialogProps = {
  onClose: () => void;
  confirmationProps: ExportConfirmationProps | undefined;
  defaultExportOptions?: ExportOptions;
};
const ExportOptionsDialog = forwardRef<HTMLDivElement, ExportOptionsDialogProps>((t0, ref) => {
  const $ = _c(39);
  const {
    onClose,
    confirmationProps,
    defaultExportOptions: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {
      type: ExportType.Release
    } : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const defaultExportOptions = t2;
  const cancelRef = useRef(null);
  const [options, setOptions] = useState(defaultExportOptions);
  const [historyLimit, setHistoryLimitInput] = useState(1000);
  let t3;
  if ($[2] !== confirmationProps || $[3] !== onClose || $[4] !== options) {
    t3 = () => {
      if (!confirmationProps) {
        throw new Error("Confirmation props must be defined to confirm export (ExportConfirmationDialog.tsx)");
      }
      confirmationProps.onConfirm(options);
      onClose();
    };
    $[2] = confirmationProps;
    $[3] = onClose;
    $[4] = options;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleConfirmation = t3;
  const t4 = !!confirmationProps;
  let t5;
  if ($[6] !== onClose) {
    t5 = e => {
      if (!e.open) {
        onClose();
      }
    };
    $[6] = onClose;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = confirmationProps?.networkName;
  let t7;
  if ($[8] !== t6) {
    t7 = <DialogHeader><DialogTitle>Export network {t6}</DialogTitle></DialogHeader>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== historyLimit) {
    t8 = e_0 => {
      const value = e_0.value as ExportType;
      setOptions(value === ExportType.Release ? {
        type: value
      } : {
        type: value,
        historyLimit
      });
    };
    $[10] = historyLimit;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Radio value={ExportType.Release}>Release</Radio>;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Radio pl={4} value={ExportType.Import}>All network related data</Radio>;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== historyLimit || $[15] !== options) {
    t11 = options.type === ExportType.Import && <Flex alignItems="center" justifyContent="flex-end" pl={12}><Text pr={4}>History limit:</Text><NumberInputRoot defaultValue={historyLimit.toString()} max={8000} maxW={24} min={0} size="sm" step={1000} onValueChange={e_1 => {
        const limitInput = parseInt(e_1.value);
        setHistoryLimitInput(isNaN(limitInput) ? 0 : limitInput);
        setOptions({
          ...options,
          historyLimit: isNaN(limitInput) ? 0 : limitInput
        });
      }}><NumberInputField /></NumberInputRoot><InformationIcon info="Since the amount of histories can be quite large, we have to put a limit on it, or the export may fail." /></Flex>;
    $[14] = historyLimit;
    $[15] = options;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11) {
    t12 = <Box display="inline-block">{t10}{t11}</Box>;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== options.type || $[20] !== t12 || $[21] !== t8) {
    t13 = <DialogBody><Field label="Export type"><RadioGroup alignItems="flex-start" display="flex" value={options.type} onValueChange={t8}>{t9}{t12}</RadioGroup></Field></DialogBody>;
    $[19] = options.type;
    $[20] = t12;
    $[21] = t8;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== onClose) {
    t14 = <Button ref={cancelRef} variant="subtle" onClick={onClose}>Cancel</Button>;
    $[23] = onClose;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== handleConfirmation) {
    t15 = <Button ml={3} onClick={handleConfirmation}>Export</Button>;
    $[25] = handleConfirmation;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t14 || $[28] !== t15) {
    t16 = <DialogFooter>{t14}{t15}</DialogFooter>;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== ref || $[31] !== t13 || $[32] !== t16 || $[33] !== t7) {
    t17 = <DialogContent ref={ref} aria-label="confirmation-dialog">{t7}{t13}{t16}</DialogContent>;
    $[30] = ref;
    $[31] = t13;
    $[32] = t16;
    $[33] = t7;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t17 || $[36] !== t4 || $[37] !== t5) {
    t18 = <DialogRoot open={t4} onOpenChange={t5}>{t17}</DialogRoot>;
    $[35] = t17;
    $[36] = t4;
    $[37] = t5;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  return t18;
});
ExportOptionsDialog.displayName = 'ExportOptionsDialog';
export default ExportOptionsDialog;